import { useAuth } from "@elit-hair/elit-tools";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    Icon,
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarSeparator,
    THEMES,
    useSidebar,
    useTheme,
} from "@elit-hair/elit-ui";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbAlertCircle, TbLifebuoy, TbSettings, TbUser } from "react-icons/tb";

// TODO: Replace with TimeBasedGreeting after Elit-UI Update
import { CustomLinksPanel } from "@/components/custom-links/custom-links-panel";
import { Greeting } from "@/components/greeting";
import { SidebarLinkPanel } from "@/components/quick-access/sidebar-link-panel";
import { BrandSelector } from "@/components/sidebar/brand-selector";
import { NavigationItems } from "@/components/sidebar/navigation-items";
import { PhoneNumberSelector } from "@/components/sidebar/phone-number-selector";
import { SidebarLink } from "@/components/sidebar/sidebar-link";
import { SidebarUserButton } from "@/components/sidebar/sidebar-user-button";
import { ENV } from "@/constants/env";
import { SETTINGS_KEYS } from "@/constants/settings-keys";
import { useColorSchemeChange } from "@/hooks/use-color-scheme-change";
import { useRemoteConfigInput } from "@/pages/settings/utils/use-remote-config-input";
import { useCall } from "@/providers/hooks/use-call";
import { PermissionNames, usePermissions } from "@/providers/hooks/use-permissions";
import { useSingleInstanceContext } from "@/providers/single-instance-provider";
import { APP_ROUTES_TO } from "@/utils/routes";

export function SidebarLeft() {
    const { t } = useTranslation();
    const { userData, handleLogout } = useAuth();
    const { permissions, openModal } = usePermissions();
    const { isMultipleInstances } = useSingleInstanceContext();
    const { state } = useSidebar();
    const { isCallActive, selectedBrand, setSelectedBrand } = useCall();
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);

    const { theme, selectedColorScheme, toggleTheme, colorSchemes } = useTheme();

    const { onChange: setTheme } = useRemoteConfigInput<THEMES>(SETTINGS_KEYS.THEME);
    const handleColorSchemeChange = useColorSchemeChange();

    // Handler to change elit-ui provider & remote config
    const handleThemeToggle = useCallback(() => {
        toggleTheme();
        setTheme(theme === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT);
    }, [toggleTheme, setTheme]);

    const navigationItems = [
        {
            path: "/profile",
            name: t("profile"),
            icon: TbUser,
        },
        {
            path: "/settings",
            name: t("settings"),
            icon: TbSettings,
        },
    ];

    const missingPermissions =
        permissions[PermissionNames.MICROPHONE] !== "granted" ||
        permissions[PermissionNames.NOTIFICATIONS] !== "granted";

    return (
        <Sidebar side="left" variant="inset" collapsible="icon">
            <SidebarHeader>
                <BrandSelector
                    selectedBrand={selectedBrand}
                    onBrandChange={setSelectedBrand}
                    isCollapsed={state === "collapsed"}
                />
                <PhoneNumberSelector isCollapsed={state === "collapsed"} />
                <SidebarSeparator />
            </SidebarHeader>
            <SidebarContent>
                <SidebarGroup>
                    <SidebarGroupLabel>{t("navigation")}</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <NavigationItems />
                    </SidebarGroupContent>
                </SidebarGroup>

                <SidebarGroup>
                    <SidebarGroupLabel>{t("quick_access")}</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarLinkPanel />
                    </SidebarGroupContent>
                </SidebarGroup>

                <SidebarGroup>
                    <SidebarGroupLabel>{t("custom_links")}</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <CustomLinksPanel />
                    </SidebarGroupContent>
                </SidebarGroup>

                {isMultipleInstances && (
                    <SidebarGroup>
                        <SidebarSeparator />
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuButton
                                    className="text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/20 w-full flex items-center justify-start gap-2 min-h-0 px-4 py-2.5 rounded-lg"
                                    tooltip={state === "collapsed" ? t("multiple_instances_open") : undefined}
                                >
                                    <Icon icon={TbAlertCircle} />
                                    <span className="text-sm font-medium group-data-[collapsible=icon]:hidden">
                                        {t("multiple_instances")}
                                    </span>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroup>
                )}

                {missingPermissions && (
                    <>
                        <SidebarSeparator />
                        <SidebarGroup>
                            <SidebarGroupContent>
                                <SidebarMenu>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild className="text-destructive hover:text-destructive">
                                            <button type="button" onClick={openModal}>
                                                <TbAlertCircle />
                                                <span>{t("fix_permissions")}</span>
                                            </button>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                </SidebarMenu>
                            </SidebarGroupContent>
                        </SidebarGroup>
                    </>
                )}
            </SidebarContent>

            <SidebarFooter>
                <SidebarMenu>
                    <SidebarSeparator />
                    <SidebarMenuItem>
                        <SidebarMenuButton asChild tooltip={state === "collapsed" ? t("help_center") : undefined}>
                            <SidebarLink to={APP_ROUTES_TO.HELP_CENTER}>
                                <Icon icon={TbLifebuoy} />
                                <span>{t("help_center")}</span>
                            </SidebarLink>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarSeparator />
                    <SidebarUserButton
                        userName={userData?.name || "User"}
                        greeting={<Greeting className="text-xs leading-none text-muted-foreground" />}
                        isLogoutDisabled={isCallActive()}
                        navigationItems={navigationItems}
                        theme={theme}
                        onToggleTheme={handleThemeToggle}
                        colorScheme={selectedColorScheme}
                        colorSchemeOptions={colorSchemes}
                        onColorSchemeChange={handleColorSchemeChange}
                        onLogout={() => setShowLogoutDialog(true)}
                        logoutDialog={
                            <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>{t("logout_confirmation")}</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            {t("logout_confirmation_description")}
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                                        <AlertDialogAction
                                            onClick={() => {
                                                handleLogout();
                                                setShowLogoutDialog(false);
                                            }}
                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        >
                                            {t("logout")}
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        }
                    />
                    <SidebarSeparator />
                    <p className="text-xs text-center pt-2 text-muted-foreground">
                        <span className="group-data-[collapsible=icon]:hidden">{t("app_version")}: </span>
                        <strong>{ENV.RELEASE_VERSION}</strong>
                    </p>
                </SidebarMenu>
            </SidebarFooter>
        </Sidebar>
    );
}
